import {
  ExperienceAnchor,
  ExperienceAnchorType,
  ISupportingActor,
  UserField
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { Rule } from '@dadohr/shared/domainTypes/rules';
import styled from '@emotion/styled';
import {
  Button,
  Checkbox,
  FormControlLabel
} from '@material-ui/core';
import { keyBy } from 'lodash';
import { Edit, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

import { ColumnSelector } from '../../../../../components/ColumnSelector';
import { TableSearch } from '../../../../../components/Table';
import { UserFilterDialog } from '../../../../../components/UserFilterDialog';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { useUnsafeTranslation } from '../../../../../i18next';
import { Centered } from '../../../../../layout/Centered';
import { FlexContainer } from '../../../../../layout/Flex';
import { useCurrentUser } from '../../../../../services/currentUser';
import { SelectableParticipantWithActors } from '../../../../../services/experiences/template';
import { isTimestamp, now, Timestamp } from '../../../../../services/time';
import {
  getUserValue,
  matchUser,
  matchUserBySearchTerm,
  toSearchTermRegexp
} from '../../../../../services/user';
import { AnchorField } from '../../../components/Anchors';
import { isEmptyRule } from '../../../components/RuleBuilder';
import { getAnchorLabel } from '../../TemplateEdit/EnrollmentPreview';
import {
  CONTACT_MANUAL_ENROLL_COLUMNS,
  OTHER_MANUAL_ENROLL_COLUMNS,
  OtherProps,
  REQUIRED_MANUAL_ENROLL_COLUMNS,
  useManualEnrollTableColumnsAndSorters
} from './columns';
import { ManualEnrollTable } from './ManualEnrollTable';

const ContentLayout = styled('div')((p) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  gap: p.theme.spacing(2),
  maxHeight: '100%'
}));

const TableContainer = styled('div')((p) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  overflowY: 'scroll'
}));

const StickyFilterContainer = styled(FlexContainer)((p) => ({
  position: 'sticky',
  background: p.theme.palette.background.paper,
  zIndex: 99,

  // This is all in relation to other paddings within the component this is used in,
  // specifically a dialog
  marginTop: p.theme.spacing(-2),
  paddingTop: p.theme.spacing(2),
  top: p.theme.spacing(0)
  // marginBottom: p.theme.spacing(2)
}));

export const SelectParticipantDialogContent = ({
  selected,
  setSelected,
  selectableParticipants,
  actors,
  templateAnchor,
  anchorFields,

  orgId,
  customFields,
  tz,
  templateRules,
  n,
  experienceTemplateId
}: {
  selected: Set<string>;
  setSelected: React.Dispatch<React.SetStateAction<Set<string>>>;
  selectableParticipants: SelectableParticipantWithActors[];
  actors: {
    [key: string]: ISupportingActor;
  };
  templateAnchor: ExperienceAnchor;
  anchorFields: AnchorField[];

  orgId: string;
  customFields: CustomUserField[];
  tz: string;
  templateRules: Rule<UserField> | null;
  n: Timestamp;
  experienceTemplateId: string;
}) => {
  const { org } = useCurrentUser();
  const { t } = useUnsafeTranslation();
  const anchorLabel = getAnchorLabel(templateAnchor, anchorFields, t) || '';
  const [q, setQ] = useState('');
  const [showUnselectableUsers, setShowUnselectableUsers] = useState(false);

  const filterDialog = useDialogState();
  const [filters, setFilters] = useState<Rule<UserField> | null>(null);

  const baseOtherProps = useMemo(() => {
    return {
      selected,
      templateAnchor,
      anchorLabel,
      n
    };
  }, [selected, templateAnchor, anchorLabel, n]);

  const allRows = useMemo(() => {
    const rows = selectableParticipants.map((s) => ({
      participant: s.participant,
      supportingActors: keyBy(s.supportingActors, 'key'),
      isSelectable:
        templateAnchor.type !== ExperienceAnchorType.EMPLOYEE_DATE
          ? true
          : isTimestamp(
              getUserValue(s.participant.data, templateAnchor.field, n)
            )
    }));
    return rows;
  }, [selectableParticipants, templateAnchor, n]);

  const showShowUnselectableUsersCheckbox = useMemo(() => {
    return !!allRows.find((r) => !r.isSelectable);
  }, [allRows]);

  const filteredRows = useMemo(() => {
    let nextRows = allRows;
    if (!showUnselectableUsers) {
      nextRows = nextRows.filter((u) => u.isSelectable);
    }
    if (q) {
      const regexp = toSearchTermRegexp(q);
      nextRows = nextRows.filter((u) =>
        matchUserBySearchTerm(regexp, u.participant)
      );
    }
    if (filters && !isEmptyRule(filters)) {
      // Slightly imprecise - should be whatever the anchor would be
      const n = now();
      nextRows = nextRows.filter((u) =>
        matchUser(
          u.participant,
          filters,
          templateAnchor.type === ExperienceAnchorType.EMPLOYEE_DATE
            ? getUserValue(u.participant.data, templateAnchor.field, n)
            : n
        )
      );
    }
    return nextRows;
  }, [allRows, q, filters, showUnselectableUsers, templateAnchor]);

  const ManualEnrollColumns = useMemo(() => {
    const actorKeys = Object.keys(actors);
    const anchorExists =
      templateAnchor.type === ExperienceAnchorType.EMPLOYEE_DATE;

    let columns = [
      ...REQUIRED_MANUAL_ENROLL_COLUMNS,
      ...CONTACT_MANUAL_ENROLL_COLUMNS,
      ...actorKeys,
      ...OTHER_MANUAL_ENROLL_COLUMNS
    ];

    // Add anchor if it exists
    if (anchorExists) {
      columns.push('anchorDate');
    }
    if (
      templateAnchor.type !== ExperienceAnchorType.EMPLOYEE_DATE ||
      templateAnchor.field !== 'hireDate'
    ) {
      columns.push('hireDate');
    }

    return columns;
  }, [actors, templateAnchor]);

  const { columns, sorters } = useManualEnrollTableColumnsAndSorters(
    Object.values(actors),
    ManualEnrollColumns
  );

  const [selectedColumns, setSelectedColumns] = useState(
    columns.filter((c) => {
      return !(
        c.key === 'email' ||
        c.key === 'personalEmail' ||
        c.key === 'phoneNumber'
      );
    })
  );

  const hasFiltersApplied = filters && !isEmptyRule(filters);

  const otherProps = useMemo<OtherProps>(() => {
    return {
      ...baseOtherProps,
      selectAllCurrentlyVisible: () =>
        setSelected(
          (prev) =>
            new Set([...prev, ...filteredRows.map((d) => d.participant.id)])
        ),
      deselectAllCurrentlyVisible: () => {
        setSelected((prev) => {
          const nextSelected = new Set([...prev]);
          filteredRows.forEach((d) => nextSelected.delete(d.participant.id));
          return nextSelected;
        });
      },
      areCurrentAllSelected: filteredRows.every((r) =>
        baseOtherProps.selected.has(r.participant.id)
      ),
      dateFormat: org.dateFormats.MMDDYYYY,
      tz: org.tz
    };
  }, [baseOtherProps, setSelected, filteredRows, org.dateFormats.MMDDYYYY, org.tz]);

  return (
    <ContentLayout>
      <StickyFilterContainer>
        <FlexContainer justifyContent="space-between" fullWidth>
          {showShowUnselectableUsersCheckbox ? (
            <FormControlLabel
              label="Show unselectable participants"
              control={
                <Checkbox
                  checked={showUnselectableUsers}
                  onChange={(ev) => {
                    setShowUnselectableUsers(ev.target.checked);
                  }}
                />
              }
            />
          ) : (
            <div />
          )}

          <FlexContainer justifyContent="flex-end" spacing={1}>
            <Button
              color="primary"
              size="small"
              variant={hasFiltersApplied ? 'contained' : 'text'}
              startIcon={
                hasFiltersApplied ? <Edit size={12} /> : <Plus size={12} />
              }
              onClick={() => filterDialog.openDialog()}
            >
              {hasFiltersApplied ? t('Edit filters') : t('Add filters')}
            </Button>
            <ColumnSelector
              value={selectedColumns}
              onChange={setSelectedColumns}
              columns={columns}
              requiredColumns={new Set(['name'])}
              checkBoxColumnKey={'selector'}
              otherProps={otherProps}
            />
            <TableSearch
              term={q}
              onChange={setQ}
              placeholder={t('users.overview.search')}
            />
          </FlexContainer>
        </FlexContainer>
      </StickyFilterContainer>

      {filteredRows.length !== 0 && (
        <TableContainer>
          <ManualEnrollTable
            ds={filteredRows}
            selected={selected}
            onChangeSelected={setSelected}
            columns={selectedColumns}
            sorters={sorters}
            templateAnchor={templateAnchor}
            anchorFields={anchorFields}
            headProps={{ sticky: true }}
            otherProps={otherProps}
          />
        </TableContainer>
      )}
      {filteredRows.length === 0 && <Centered>{t('No users found')}</Centered>}

      {filterDialog.dialogOpen && (
        <UserFilterDialog
          open={true}
          onClose={filterDialog.closeDialog}
          value={filters}
          onChange={setFilters}
          orgId={orgId}
          customFields={customFields}
          tz={tz}
          templateRules={templateRules}
          experienceTemplateId={experienceTemplateId}
        />
      )}
    </ContentLayout>
  );
};
